import { v4 } from 'uuid';

import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { DatePipe } from '@progress/kendo-angular-intl';

// Tokens
import { APPLICATION_INSTANCE_ID, HttpClientBase, LvApplicationVersionService, LvLocalDatePipe } from '@leversys/leversys-core-ui';
import { LvLoggerService, LvErrorService } from '@leversys/leversys-core-ui';

// Modules
import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';

// External Components
import { AnotherInstanceComponent } from './views/external/another-instance/another-instance.component';
import { ExternalComponent } from './views/external/external.component';
import { UnauthorizedComponent } from './views/external/unauthorized/unauthorized.component';
import { PageNotFoundComponent } from './views/external/page-not-found/page-not-found.component';
import { NotSupportedBrowserComponent } from './views/external/not-supported-browser/not-supported-browser.component';
import { AnotherInstanceModalComponent } from './views/external/another-instance-modal/another-instance-modal.component';

// Kendo Modules
import { TooltipSettings } from '@progress/kendo-angular-tooltip';

// Other Services
import { LvLocalization } from '@leversys/leversys-core-ui';

// Core UI Modals
import { LeversysCoreUIModule } from '@leversys/leversys-core-ui';
import { LeversysCoreUIModalsModule } from '@leversys/leversys-core-ui';

// Localization
import '@progress/kendo-angular-intl/locales/en/all';
import localeEn from '@angular/common/locales/en';
import '@progress/kendo-angular-intl/locales/en-GB/all';
import localeenGb from '@angular/common/locales/en-GB';
import '@progress/kendo-angular-intl/locales/sr/all';
import localeSr from '@angular/common/locales/sr';
import '@progress/kendo-angular-intl/locales/nb/all';
import localeNb from '@angular/common/locales/nb';
import '@progress/kendo-angular-intl/locales/sv/all';
import localeSv from '@angular/common/locales/sv';
import '@progress/kendo-angular-intl/locales/zh/all';
import localeZh from '@angular/common/locales/zh';
import '@progress/kendo-angular-intl/locales/fr/all';
import localeFr from '@angular/common/locales/fr';
import '@progress/kendo-angular-intl/locales/nl/all';
import localeNl from '@angular/common/locales/nl';
import '@progress/kendo-angular-intl/locales/it/all';
import localeIt from '@angular/common/locales/it';
import '@progress/kendo-angular-intl/locales/es/all';
import localeEs from '@angular/common/locales/es';
import '@progress/kendo-angular-intl/locales/de/all';
import localeDe from '@angular/common/locales/de';
import '@progress/kendo-angular-intl/locales/nb/all';
import localeNo from '@angular/common/locales/nb';
import '@progress/kendo-angular-intl/locales/af/all';
import localeAf from '@angular/common/locales/af';
import '@progress/kendo-angular-intl/locales/he/all';
import localeHe from '@angular/common/locales/he';
import '@progress/kendo-angular-intl/locales/pt/all';
import localePt from '@angular/common/locales/pt';
import '@progress/kendo-angular-intl/locales/da/all';
import localeDa from '@angular/common/locales/da';
import '@progress/kendo-angular-intl/locales/fi/all';
import localeFi from '@angular/common/locales/fi';
import '@progress/kendo-angular-intl/locales/zh-Hans/all';
import localeZhHans from '@angular/common/locales/zh-Hans';
import '@progress/kendo-angular-intl/locales/ja/all';
import localeJa from '@angular/common/locales/ja';
import '@progress/kendo-angular-intl/locales/ko/all';
import localeKo from '@angular/common/locales/ko';
import '@progress/kendo-angular-intl/locales/vi/all';
import localeVi from '@angular/common/locales/vi';
import '@progress/kendo-angular-intl/locales/en-AU/all';
import localeEnAu from '@angular/common/locales/en-AU';
import '@progress/kendo-angular-intl/locales/en-CA/all';
import localeEnCa from '@angular/common/locales/en-CA';

import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { InternalModule, LvExcelService, SecurityModule } from '@leversys/leversys-frontend-core'
import { RouteCacheService } from '@leversys/leversys-frontend-core';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InternalCoreModule } from '@leversys/leversys-frontend-core';

import { ApplicationSettingsModule } from '@leversys/leversys-frontend-core';
import { AnalyticsHubService } from '@leversys/leversys-frontend-core';
import { ApiService } from '@leversys/leversys-frontend-core';
import { RouteCacheStrategy } from '@leversys/leversys-frontend-core';
import { FormsModule } from '@angular/forms';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { EditorModule } from '@progress/kendo-angular-editor';
import { AppRoutingModule } from './app.routing';
import { BaseRouteReuseStrategy, RouteReuseStrategy } from '@angular/router';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeenGb, 'en-GB');
registerLocaleData(localeSr, 'sr');
registerLocaleData(localeNb, 'nb');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeZh, 'zh');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeNo, 'nb');
registerLocaleData(localeAf, 'af');
registerLocaleData(localeHe, 'he');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeZhHans, 'zh-Hans');
registerLocaleData(localeJa, 'ja');
registerLocaleData(localeKo, 'ko');
registerLocaleData(localeVi, 'vi');
registerLocaleData(localeEnAu, 'en-AU');
registerLocaleData(localeEnCa, 'en-CA');


@NgModule({
  declarations: [
    AppComponent,
    ExternalComponent,
    UnauthorizedComponent,
    PageNotFoundComponent,
    NotSupportedBrowserComponent,
    AnotherInstanceComponent,
    AnotherInstanceModalComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    ToastrModule.forRoot({
      disableTimeOut: environment.ngxToastr.disableTimeOut,
      enableHtml: true
    }),

    // Kendo modules
    InputsModule,
    LabelModule,
    DropDownsModule,
    DateInputsModule,
    ButtonsModule,
    EditorModule,

    // Core UI Modals
    LeversysCoreUIModalsModule,

    // Internal Module
    InternalCoreModule.forRoot(environment),
    InternalModule,

    // Other
    SecurityModule.forRoot(environment.identityServer.apiUrl),
    ApplicationSettingsModule.forRoot(environment.apiUrl),
    AppRoutingModule,
    LoggerModule.forRoot({
      serverLoggingUrl: environment.errorLoggerUrl,
      level: NgxLoggerLevel.ERROR,
      serverLogLevel: NgxLoggerLevel.INFO,
    })
  ],
  providers: [
    DatePipe,
    // HttpClientBase,
    LvExcelService,
    ApiService,
    LvErrorService,
    RouteCacheService,
    LvLoggerService,
    LvApplicationVersionService,
    LvLocalDatePipe,
    AnalyticsHubService,
    {
      provide: APPLICATION_INSTANCE_ID,
      useFactory: () => v4()
    },
    {
      provide: TooltipSettings,
      useFactory: (): TooltipSettings => ({
        showOn: environment.showTooltips ? 'hover' : 'none',
      } as TooltipSettings)
    },
    {
      provide: LOCALE_ID,
      useFactory: () => LvLocalization.getBrowserLocale()
    },
    { provide: ICON_SETTINGS, useValue: { type: 'svg' } },
    { provide: RouteReuseStrategy, useClass: RouteCacheStrategy }

  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
